import React, { ReactElement } from 'react';
import { Image } from '../../../commonTypes/image';
import { CallToAction } from '../../../commonTypes/links';
import { VideoType } from '../../../commonTypes/video';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import ResponsiveVideo from '../../Video/ResponsiveVideo';
import {
  Body,
  BottomSpacing,
  CtasContainer,
  Heading,
  MainImage,
  MediaContainer,
  MediaWrapper,
  ReversibleDiv,
  StyledPrimaryCta,
  StyledSecondaryCta,
  Subhead,
  TextContainer,
  TextContent,
} from './styles';

export type HeroWithResponsiveMediaProps = {
  heading: string;
  subhead?: string;
  body?: string;
  image?: Image;
  video?: VideoType;
  primaryCta?: CallToAction;
  secondaryCta?: CallToAction;
  bottomSpaceHeight?: number;
  addBackgroundColourToBottomSpace?: boolean;
  CookieNoticeComponent?: React.ComponentType;
} & ComponentProps;

// The unit for bottom_space_height is layout.baseSpacingPx
// See, for instance, DigitalSharedComponents\theme\<brand>\layout.ts
// Hence, if, for instance, layout.baseSpacingPx equals 8 (pixels)
// a value of '3' is equivalent to 24px

const HeroWithResponsiveMedia: React.FC<HeroWithResponsiveMediaProps> = ({
  heading,
  subhead,
  body,
  image,
  video,
  primaryCta,
  secondaryCta,
  CookieNoticeComponent,
  bottomSpaceHeight,
  addBackgroundColourToBottomSpace,
  ...props
}) => {
  const hasMedia = !!image || !!video;
  const hasCtas = !!primaryCta || !!secondaryCta;
  const showBottomSpacing = !!bottomSpaceHeight; // bottomSpaceHeight is defined and > 0

  const Media = (
    img: Image | undefined,
    vid: VideoType | undefined
  ): ReactElement | undefined => {
    if (img) {
      return (
        <MediaWrapper>
          <MainImage role="img" tabIndex={0} image={img} aria-label={img.altText} />
        </MediaWrapper>
      );
    } else if (vid) {
      return (
        <MediaWrapper>
          <ResponsiveVideo
            src={vid.src}
            screenReaderText={vid.screenReaderText}
            videoCookiePolicyData={vid.videoCookiePolicyData}
          />
        </MediaWrapper>
      );
    } else {
      return undefined;
    }
  };

  return (
    <>
      <ReversibleDiv {...componentProps(props)}>
        <TextContainer hasMedia={hasMedia} tabIndex={0}>
          <TextContent hasCtas={hasCtas}>
            <Heading>{heading}</Heading>
            {subhead && <Subhead>{subhead}</Subhead>}
            {body && <Body html={body}></Body>}
          </TextContent>
          {hasCtas && (
            <CtasContainer noOfSections={1}>
              {primaryCta && <StyledPrimaryCta cta={primaryCta} />}
              {secondaryCta && <StyledSecondaryCta cta={secondaryCta} />}
            </CtasContainer>
          )}
        </TextContainer>
        {hasMedia && <MediaContainer>{Media(image, video)}</MediaContainer>}
      </ReversibleDiv>
      {showBottomSpacing && (
        <BottomSpacing
          spaceHeight={bottomSpaceHeight ?? 0}
          addBackgroundColour={!!addBackgroundColourToBottomSpace}
        />
      )}
    </>
  );
};

export default applyComponentTheme(HeroWithResponsiveMedia, 'HeroWithResponsiveMedia');
